import React, { FC, Fragment, ReactElement } from 'react';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheUISlider, RocheUISlideAnimation } from '@repo/roche-ui';
import { blokToComponent } from '../../helpers/blok-to-component';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { StringService } from '../../services/string';
import { useTranslation } from '../../hooks/use-translation';

type RocheUISliderProps = BlokToComponentProps<{
  _uid?: string;
  slides_per_page: string;
  animation: RocheUISlideAnimation;
  slotted: any;
}>;

export const RocheSlider: FC<RocheUISliderProps> = ({ blok, getComponent, slot }): JSX.Element => {
  // eslint-disable-next-line no-underscore-dangle
  const id = blok._uid || StringService.generate();
  const slidesPerPage = parseInt(blok.slides_per_page, 10);
  const animation = blok.animation || RocheUISlideAnimation.ENTER_FROM_RIGHT;
  const nextSlideLabel = useTranslation('next slide label');
  const prevSlideLabel = useTranslation('previous slide label');

  const sliderHasChildren: boolean = Array.isArray(blok.slotted);
  const children: ReactElement[] = sliderHasChildren
    ? blok.slotted.map((blokItem) => {
        // The roche slider children can be constructed with many other elements:
        if (Array.isArray(blokItem.slotted)) {
          return blokItem.slotted.map((slottedBlokItem) => {
            return (
              // eslint-disable-next-line no-underscore-dangle
              <Fragment key={slottedBlokItem._uid || StringService.generate()}>
                {blokToComponent({ blok: slottedBlokItem, getComponent })}
              </Fragment>
            );
          });
        }
        return null;
      })
    : null;

  return (
    sliderHasChildren && (
      <RocheUISlider
        id={id}
        slidesPerPage={slidesPerPage}
        nextSlideLabel={nextSlideLabel}
        prevSlideLabel={prevSlideLabel}
        animation={animation}
        trackSlide={trackSlide}
        slot={slot}
      >
        {children}
      </RocheUISlider>
    )
  );
};

const defaultTracking = {
  event: 'slider_changed',
  component_name: 'roche-slider',
};

const trackSlide = (activeIndex: number, previousIndex: number, id: string): void => {
  TrackingService.push({
    ...defaultTracking,
    value: activeIndex > previousIndex ? 'next' : 'previous',
    additionalParameters: {
      slideId: id,
      activeIndex,
      previousIndex,
    },
  });
};
